import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, XCircle, CreditCard, Upload, X, AlertTriangle, Building2 } from "lucide-react";
import api, { formatRON, formatApiError, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const emptyForm = { company: "", brand: "", logo: "", website: "", description: "", categories: [], contact_email: "", contact_phone: "" };

const statusBanner = {
  pending: { icon: Clock, cls: "bg-amber-500/10 border-amber-500/40 text-amber-300", label: "În verificare", text: "Profilul tău de partener este în curs de verificare de către echipa OXO." },
  verified: { icon: CheckCircle2, cls: "bg-green-500/10 border-green-500/40 text-green-300", label: "Verificat", text: "Profil verificat. Activează sponsorizarea prin plata abonamentului anual." },
  rejected: { icon: XCircle, cls: "bg-red-500/10 border-red-500/40 text-red-300", label: "Respins", text: "Profilul a fost respins. Editează și retrimite." },
};

export default function SponsorProfile() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [profile, setProfile] = useState(undefined);
  const [fee, setFee] = useState(4800);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const polled = useRef(false);

  const loadProfile = useCallback(() =>
    api.get("/my-sponsor-profile").then((r) => {
      setProfile(r.data);
      if (r.data) setForm({ ...emptyForm, ...r.data, categories: r.data.categories || [], logo: r.data.logo && !r.data.logo.startsWith("http") ? r.data.logo : "" });
    }).catch(() => setProfile(null)), []);

  useEffect(() => {
    api.get("/sponsors/options").then((r) => setFee(r.data.fee_annual)).catch(() => {});
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Sponsorizare activată! Produsele tale apar acum cu badge de partener oficial."); setParams({}); loadProfile(); return; }
        if (data.status === "expired" || attempts >= 6) { toast.error("Plata nu a fost confirmată."); setParams({}); return; }
        setTimeout(poll, 2000);
      } catch { setParams({}); }
    };
    poll();
  }, [sessionId, setParams, loadProfile]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/sponsors/upload-logo", fd);
      setForm((f) => ({ ...f, logo: r.data.path }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eroare la încărcare"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!form.company.trim()) return toast.error("Adaugă numele companiei");
    if (!form.brand.trim()) return toast.error("Adaugă marca (brand) — produsele cu această marcă vor primi badge-ul de partener");
    if (!form.contact_email.trim()) return toast.error("Adaugă un email de contact");
    setSaving(true);
    try {
      await api.post("/sponsors", { ...form, categories: typeof form.categories === "string" ? form.categories.split(",").map((s) => s.trim()).filter(Boolean) : form.categories });
      toast.success("Profil trimis spre verificare.");
      loadProfile();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/sponsors/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPaying(false); }
  };

  if (profile === undefined) return <div className="bg-slate-950 text-white min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const banner = profile ? statusBanner[profile.status] : null;
  const categoriesStr = Array.isArray(form.categories) ? form.categories.join(", ") : form.categories;

  return (
    <div className="bg-slate-950 text-white min-h-screen" data-testid="sponsor-profile-page">
      <div className="w-full max-w-[98%] mx-auto px-2 sm:px-3 py-4">
        <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">Program parteneri</span>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter mt-2">{profile ? "Gestionează sponsorizarea" : "Devino producător partener OXO"}</h1>
        <p className="text-slate-400 text-sm mt-2">Apari în secțiunea „Parteneri tehnologici", primești o pagină dedicată, iar produsele mărcii tale capătă badge-ul <span className="text-amber-400 font-bold">Partener oficial</span> și prioritate în catalog.</p>
        <div className="border border-orange-900/50 bg-orange-950/20 px-5 py-4 mt-5 text-sm text-slate-300" data-testid="sponsor-fee-note">
          Abonament anual de sponsorizare: <span className="font-bold text-orange-400">{formatRON(fee)}/an</span>.
        </div>

        {banner && (
          <div className={`border px-5 py-4 mt-6 flex items-start gap-3 ${banner.cls}`} data-testid="sponsor-status-banner">
            <banner.icon className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold flex items-center gap-2">{banner.label}{profile.is_public && <span className="text-green-400 text-xs">· Public</span>}</div>
              <p className="text-sm opacity-90 mt-0.5">{profile.status === "rejected" && profile.reject_reason ? `Motiv: ${profile.reject_reason}` : banner.text}</p>
            </div>
          </div>
        )}

        {profile && profile.expired && (
          <div className="border border-red-500/40 bg-red-500/10 text-red-300 px-5 py-4 mt-4 flex items-start gap-3" data-testid="sponsor-expired-banner">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div><div className="font-bold">Sponsorizare expirată</div><p className="text-sm opacity-90 mt-0.5">Badge-ul a fost retras. Reînnoiește pentru a reapărea.</p></div>
          </div>
        )}

        {profile && profile.is_public && profile.expiring_soon && !profile.expired && (
          <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 px-5 py-4 mt-4 flex items-start gap-3" data-testid="sponsor-expiring-banner">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div><div className="font-bold">Sponsorizarea expiră în {profile.days_left} {profile.days_left === 1 ? "zi" : "zile"}</div><p className="text-sm opacity-90 mt-0.5">Reînnoiește acum pentru a păstra badge-ul „Partener oficial" și prioritatea în catalog, fără întrerupere.</p></div>
          </div>
        )}

        {profile && profile.status === "verified" && (!profile.active || profile.expired || profile.expiring_soon) && (
          <button onClick={pay} disabled={paying} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="pay-sponsorship">
            <CreditCard className="w-4 h-4" /> {paying ? "Se redirecționează..." : `${(profile.expired || profile.expiring_soon) && profile.active ? "Reînnoiește sponsorizarea" : profile.expired ? "Reînnoiește sponsorizarea" : "Activează sponsorizarea"} · ${formatRON(profile.annual_fee || fee)}/an`}
          </button>
        )}

        {profile && profile.is_public && profile.paid_until && (
          <p className="text-xs text-slate-500 mt-2" data-testid="sponsor-paid-until">Sponsorizare activă până la {new Date(profile.paid_until).toLocaleDateString("ro-RO")}.</p>
        )}

        <div className="space-y-5 mt-8">
          <div className="flex items-center gap-5">
            <div className="w-24 h-24 bg-slate-800 border border-slate-700 overflow-hidden shrink-0 flex items-center justify-center">
              {form.logo ? <img src={mediaUrl(form.logo)} alt="" className="w-full h-full object-contain" /> : <Building2 className="w-8 h-8 text-slate-600" />}
            </div>
            <label className="inline-flex items-center gap-2 border border-slate-700 hover:border-orange-600 px-4 py-2.5 text-sm cursor-pointer transition-colors" data-testid="sponsor-logo-label">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Încarcă logo
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} data-testid="sponsor-logo-input" />
            </label>
            {form.logo && <button onClick={() => setForm({ ...form, logo: "" })} className="text-slate-400 hover:text-red-400"><X className="w-4 h-4" /></button>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Companie *</Label><Input value={form.company} onChange={set("company")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-company" /></div>
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Marcă / Brand *</Label><Input value={form.brand} onChange={set("brand")} placeholder="Ex: SCM" className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-brand" /></div>
          </div>

          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Descriere</Label><Textarea value={form.description} onChange={set("description")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5 min-h-[110px]" data-testid="sponsor-description" /></div>

          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Categorii produse (separate prin virgulă)</Label><Input value={categoriesStr} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="Ex: Freze, CNC, Șlefuire" className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-categories" /></div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Email contact *</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Telefon</Label><Input value={form.contact_phone} onChange={set("contact_phone")} className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-phone" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Website</Label><Input value={form.website} onChange={set("website")} placeholder="https://..." className="rounded-none bg-slate-900 border-slate-700 text-white mt-1.5" data-testid="sponsor-website" /></div>

          <button onClick={submit} disabled={saving || uploading} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="submit-sponsor">
            {saving ? "Se trimite..." : profile ? "Salvează și retrimite spre verificare" : "Trimite profilul spre verificare"}
          </button>
        </div>
      </div>
    </div>
  );
}
